import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import {
  Search,
  RestartAlt,
  LastPage,
  FirstPage,
  KeyboardArrowRight,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import thLocale from "date-fns/locale/th";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AppContainer from "src/components/AppContainer";
import css from "./adminList.module.scss";
import { useStore } from "src/providers/store";
import { IPaging } from "src/models/paging";
import { IVehicle } from "src/models/vehicle";

const AdminListPage = () => {
  const { vehicle } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    const filter = await vehicle.getFilter();
    setTravelType(filter?.travelType ? filter?.travelType : "arrival");
    setDate(filter?.date ? new Date(filter?.date) : new Date());
    setApproveStatus(filter?.approveStatus ? filter?.approveStatus : "all");
    setVehicleType(filter?.vehicleType ? filter?.vehicleType : "all");
    setLicensePlate(filter?.licensePlate ? filter?.licensePlate : "");
    setProvince(filter?.province ? filter?.province : "");
    setCountry(filter?.country ? filter?.country : "");
    onSearch(filter);
  };

  const [isLoading, setLoading] = useState(false);
  const [paging, setPaging] = useState<IPaging<IVehicle>>({
    total: 0,
    pageNumber: 0,
    pageSize: 0,
    totalPage: 0,
    items: [],
  });

  const [travelType, setTravelType] = useState("arrival");
  const [date, setDate] = useState<Date | null>(new Date());
  const [approveStatus, setApproveStatus] = useState("all");
  const [vehicleType, setVehicleType] = useState("all");
  const [licensePlate, setLicensePlate] = useState("");
  const [province, setProvince] = useState("");
  const [country, setCountry] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const statuses = [
    { value: "all" },
    { value: "pending" },
    // { value: "reviewing" },
    { value: "approved" },
    { value: "rejected" },
    // { value: "requestMoreInfo" },
    // { value: "resubmit" },
  ];

  const vehicleTypes = [
    { value: "all" },
    { value: "car" },
    { value: "motorcycle" },
    { value: "van" },
    { value: "truck" },
  ];

  const onChangeTravelType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTravelType((event.target as HTMLInputElement).value);
  };
  const onChangeApproveStatus = (event) => {
    setApproveStatus(event.target.value);
  };
  const onChangeVehicleType = (event) => {
    setVehicleType(event.target.value);
  };
  const onChangeLicensePlate = (event) => {
    setLicensePlate(event.target.value);
  };
  const onChangeProvince = (event) => {
    setProvince(event.target.value);
  };
  const onChangeCountry = (event) => {
    setCountry(event.target.value);
  };

  const resetSearch = async () => {
    setTravelType("arrival");
    setDate(new Date());
    setApproveStatus("all");
    setVehicleType("all");
    setLicensePlate("");
    setProvince("");
    setCountry("");
    const filter = {
      travelType: "arrival",
      date: new Date(new Date().setHours(0, 0, 0, 0)),
      approveStatus: undefined,
      vehicleType: undefined,
      licensePlate: undefined,
      province: undefined,
      country: undefined,
    };
    onSearch(filter);
  };
  const onSearch = async (filter?: any) => {
    setLoading(true);
    console.log(filter);
    if (!filter) {
      filter = {
        travelType: travelType,
        date: date ? new Date(date.setHours(0, 0, 0, 0)) : undefined,
        approveStatus: approveStatus === "all" ? undefined : approveStatus,
        vehicleType: vehicleType === "all" ? undefined : vehicleType,
        licensePlate: licensePlate,
        province: province,
        country: country,
      };
    }
    await vehicle.setFilter(filter);
    await vehicle.getVehicleApi(filter);
    await setPaging(vehicle.getVehicles());
    setLoading(false);
  };

  const onClickRow = (id: string) => {
    navigate("/admin-detail", { state: id });
  };
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const dateDisplay = (date: Date) => {
    date.setHours(date.getHours() + 7);
    return date.toISOString().slice(0, 10).split("-").reverse().join("/");
  };

  return (
    <AppContainer
      showAppBar
      showUser
      isLoading={isLoading}
      title={t("adminListPage.title")}
      className={css.appContainer}
    >
      <Container className={css.container}>
        <Card sx={{ minWidth: 275 }} className={css.card}>
          <CardHeader title={t("adminListPage.searchInfo")}></CardHeader>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>{t("travelType")}</FormLabel>
                <RadioGroup
                  value={travelType}
                  onChange={onChangeTravelType}
                  name="radio-buttons-group"
                  row
                >
                  <FormControlLabel
                    value="arrival"
                    control={<Radio />}
                    label={t<string>("arrival")}
                  />
                  <FormControlLabel
                    value="departure"
                    control={<Radio />}
                    label={t<string>("departure")}
                  />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  locale={thLocale}
                >
                  <DatePicker
                    label={t("adminListPage.date")}
                    value={date}
                    onChange={(newDate) => {
                      setDate(newDate);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("adminListPage.status")}</InputLabel>
                  <Select
                    id="approveStatus"
                    value={approveStatus}
                    label={t("adminListPage.status")}
                    onChange={onChangeApproveStatus}
                  >
                    {statuses.map((status, index) => (
                      <MenuItem key={index} value={status.value}>
                        {t("constant.status." + status.value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("vehicleType")}</InputLabel>
                  <Select
                    id="vehicleType"
                    label={t("vehicleType")}
                    value={vehicleType}
                    onChange={onChangeVehicleType}
                  >
                    {vehicleTypes.map((vehicleType, index) => (
                      <MenuItem key={index} value={vehicleType.value}>
                        {t("constant.vehicleType." + vehicleType.value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                {" "}
                <TextField
                  id="licensePlate"
                  label={t("registration")}
                  variant="outlined"
                  placeholder={t("registration")}
                  value={licensePlate}
                  onChange={onChangeLicensePlate}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="province"
                  label={t("province")}
                  variant="outlined"
                  placeholder={t("province")}
                  value={province}
                  onChange={onChangeProvince}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="country"
                  label={t("country")}
                  variant="outlined"
                  placeholder={t("country")}
                  value={country}
                  onChange={onChangeCountry}
                  fullWidth
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={css.cardAction}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<RestartAlt />}
              onClick={() => resetSearch()}
            >
              {t("adminListPage.reset")}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<Search />}
              onClick={() => onSearch()}
            >
              {t("adminListPage.search")}
            </Button>
          </CardActions>
        </Card>

        <Card sx={{ minWidth: 275 }} className={css.card}>
          <CardHeader title={t("adminListPage.listInfo")}></CardHeader>
          <CardContent>
            {paging?.items.length === 0 && (
              <Box className={css.notFound}>
                <Search className={css.icon} />
                {t("adminListPage.notFound")}
              </Box>
            )}
            {paging?.items.length > 0 && (
              <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer>
                  <Table sx={{ minWidth: 650 }} size="medium">
                    <TableHead className={css.tableHeader}>
                      <TableRow>
                        <TableCell>{t("adminListPage.date")}</TableCell>
                        <TableCell align="center">{t("travelType")}</TableCell>
                        <TableCell align="center">{t("vehicleType")}</TableCell>
                        <TableCell align="center">
                          {t("registration")}
                        </TableCell>
                        <TableCell align="center">{t("province")}</TableCell>
                        <TableCell align="center">{t("country")}</TableCell>
                        <TableCell align="right">
                          {t("adminListPage.status")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paging?.items.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                          className={css.tableRow}
                          onClick={() => onClickRow(row._id)}
                        >
                          <TableCell component="th" scope="row">
                            {row.created?.date
                              ? dateDisplay(new Date(row.created?.date))
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {t(row.travelType)}
                          </TableCell>
                          <TableCell align="center">
                            {t("constant.vehicleType." + row.vehicleType)}
                          </TableCell>
                          <TableCell align="center">
                            {row.licensePlate}
                          </TableCell>
                          <TableCell align="center">{row.province}</TableCell>
                          <TableCell align="center">{row.country}</TableCell>
                          <TableCell align="right">
                            {t("constant.status." + row.approveStatus)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={paging?.items.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            )}
          </CardContent>
        </Card>
      </Container>
    </AppContainer>
  );
};
export default observer(AdminListPage);
